import {
  isMindmapNode,
  findMindmapRoot,
  getMindmapChildren,
  getMindmapDepth,
  getMindmapBranchColor,
  getMindmapFontSize,
  CaptureUpdateAction,
} from "@excalidraw/element";

import type {
  ExcalidrawArrowElement,
  ExcalidrawElement,
  ElementsMap,
} from "@excalidraw/element/types";

import { getBoundTextElement } from "@excalidraw/element/textElement";

import { getSelectedElements } from "../scene";

import { register } from "./register";

import type { Scene } from "@excalidraw/element/Scene";

const recolorBranch = (
  parent: ExcalidrawElement,
  branchIndex: number,
  elementsMap: ElementsMap,
  scene: Scene,
) => {
  const branchColor = getMindmapBranchColor(branchIndex);
  const children = getMindmapChildren(parent, elementsMap);

  for (const child of children) {
    const depth = getMindmapDepth(child, elementsMap);
    const fontSize = getMindmapFontSize(depth);

    // Recolor the node
    scene.mutateElement(child, {
      strokeColor: branchColor.strokeColor,
      backgroundColor: branchColor.backgroundColor,
      customData: { ...child.customData, branchIndex },
    });

    // Update bound text font size
    const boundText = getBoundTextElement(child, elementsMap);
    if (boundText) {
      scene.mutateElement(boundText, { fontSize });
    }

    // Recolor the arrow connecting parent → child
    for (const bound of child.boundElements ?? []) {
      if (bound.type !== "arrow") {
        continue;
      }
      const arrow = elementsMap.get(bound.id) as
        | ExcalidrawArrowElement
        | undefined;
      if (arrow && arrow.startBinding?.elementId === parent.id) {
        scene.mutateElement(arrow, {
          strokeColor: branchColor.strokeColor,
        });
      }
    }

    // Recurse into subtree
    recolorBranch(child, branchIndex, elementsMap, scene);
  }
};

export const actionRecolorMindMap = register({
  name: "recolorMindMap",
  label: "labels.recolorMindMap",
  viewMode: false,
  trackEvent: {
    category: "canvas",
  },
  predicate: (elements, appState) => {
    return appState.mindMapModeEnabled;
  },
  perform(elements, appState, _, app) {
    const selected = getSelectedElements(
      app.scene.getNonDeletedElementsMap(),
      appState,
    );
    if (selected.length === 0) {
      return { captureUpdate: CaptureUpdateAction.NEVER };
    }

    const firstMindmapNode = selected.find((el) => isMindmapNode(el));
    if (!firstMindmapNode) {
      return { captureUpdate: CaptureUpdateAction.NEVER };
    }

    const elementsMap = app.scene.getNonDeletedElementsMap();
    const root = findMindmapRoot(firstMindmapNode, elementsMap);

    // Update root text font size
    const rootBoundText = getBoundTextElement(root, elementsMap);
    if (rootBoundText) {
      app.scene.mutateElement(rootBoundText, {
        fontSize: getMindmapFontSize(0),
      });
    }

    // Recolor each branch with a different color
    const rootChildren = getMindmapChildren(root, elementsMap)
      .slice()
      .sort((a, b) => a.y - b.y);

    for (let i = 0; i < rootChildren.length; i++) {
      const branchColor = getMindmapBranchColor(i);

      // Color the direct child of root
      app.scene.mutateElement(rootChildren[i], {
        strokeColor: branchColor.strokeColor,
        backgroundColor: branchColor.backgroundColor,
        customData: { ...rootChildren[i].customData, branchIndex: i },
      });

      // Update bound text font size for direct child
      const childBoundText = getBoundTextElement(rootChildren[i], elementsMap);
      if (childBoundText) {
        app.scene.mutateElement(childBoundText, {
          fontSize: getMindmapFontSize(1),
        });
      }

      // Recolor arrow from root → child
      for (const bound of rootChildren[i].boundElements ?? []) {
        if (bound.type !== "arrow") {
          continue;
        }
        const arrow = elementsMap.get(bound.id) as
          | ExcalidrawArrowElement
          | undefined;
        if (arrow && arrow.startBinding?.elementId === root.id) {
          app.scene.mutateElement(arrow, {
            strokeColor: branchColor.strokeColor,
          });
        }
      }

      // Recurse into subtree
      recolorBranch(rootChildren[i], i, elementsMap, app.scene);
    }

    return {
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
});
